"use client";

import { Icon } from "@afa-shared/afa-components";
import clsx from "clsx";
import Link from "next/link";
import React, { RefObject } from "react";

import { GAClickLink, linkTracker } from "../linkTracker";
import styles from "./index.module.css";

export type StandardLinkProps = {
  href: string;
  text: string;
  trackingData?: GAClickLink;
  variant?: "link" | "button";
  buttonStyle?: "primary" | "secondary";
  ariaLabel?: string;
  color?: string;
  type?: "open_in_new" | "arrow_forward" | "file_download" | "inline";
  target?: string;
  className?: string;
  hideIcon?: boolean;
  testId?: string;
  contentfulEntryId?: string;
  contentfulFieldId?: string;
  noTracking?: boolean;
  onClick?: (value?: any) => void;
  ref?: RefObject<any>;
  rel?: string;
};

/**
 *  StandardLink is a versatile link component that can be used for both internal and external links. It supports tracking link clicks for analytics purposes and allows for various customizations such as styling, target, and rel attributes.
 * @param href The URL the link points to.
 * @param text The text to be displayed for the link.
 * @param trackingData An optional object containing additional data for tracking link clicks, such as section title, block index, link type, and insurance context.
 * @param variant The visual variant of the link, either "link" or "button".
 * @param buttonStyle The style of the button variant, either "primary" or "secondary".
 * @param ariaLabel An optional ARIA label for accessibility purposes.
 * @param color An optional color for the link text and icon.
 * @param type The type of the link, which determines the icon used (e.g., "open_in_new" for external links).
 * @param target The target attribute specifying where to open the linked document (e.g., "_blank" for new tab).
 * @param className Optional CSS class for styling the link.
 * @param hideIcon If true, hides the icon associated with the link type.
 * @param testId An optional data-testid attribute for testing purposes.
 * @param contentfulEntryId An optional data attribute for Contentful entry ID, used for tracking and preview purposes.
 * @param contentfulFieldId An optional data attribute for Contentful field ID, used for tracking and preview purposes.
 * @param noTracking If true, disables tracking for this link.
 * @param onClick An optional click handler function that will be called when the link is clicked.
 * @param ref A React ref object that can be attached to the link element.
 * @param rel The rel attribute specifying the relationship between the current document and the linked document (e.g., "noopener noreferrer" for security when using target="_blank").
 * @returns
 */
export function StandardLink({
  href,
  text,
  variant = "link",
  ariaLabel,
  color,
  type = "arrow_forward",
  target = "_self",
  className,
  buttonStyle = "primary",
  hideIcon = false,
  testId,
  contentfulEntryId,
  contentfulFieldId,
  trackingData,
  noTracking = false,
  onClick,
  ref,
  rel,
  ...rest
}: StandardLinkProps): React.ReactNode {
  if (
    process.env.NODE_ENV === "development" &&
    (!!trackingData?.sectionBlockIndex || !!trackingData?.sectionTitle) &&
    trackingData?.linkType !== "footer" &&
    !noTracking
  ) {
    console.warn(
      `Section index and title should only be undefined in specific cases, is this the case for ${text}?`,
    );
  }

  const classNames = clsx("afa-shared-link", styles.standardLink, className, buttonStyle, variant, {
    "link-external": variant === "link" && type === "open_in_new",
    "link-internal": variant === "link" && type === "arrow_forward",
    "link-file": variant === "link" && type === "file_download",
    "link-inline": variant === "link" && type === "inline",
  });

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (noTracking) {
      return;
    }

    linkTracker(href, text, trackingData, event.currentTarget);
  };

  if (type === "open_in_new") {
    return (
      <a
        href={href}
        target={target}
        ref={ref}
        rel={rel}
        onClick={handleClick}
        className={classNames}
        style={{ color }}
        aria-label={ariaLabel}
        data-testid={testId}
        data-contentful-entry-id={contentfulEntryId}
        data-contentful-field-id={contentfulFieldId}
        {...rest}
      >
        <span style={{ color }}>{text}</span>
        {!hideIcon && <Icon name={type} size="sm" fill={color} referenceSize="link" />}
      </a>
    );
  }

  return (
    <Link
      href={href}
      prefetch={false}
      onClick={handleClick}
      className={classNames}
      style={{ color }}
      target={target || "_self"}
      ref={ref}
      rel={rel}
      aria-label={ariaLabel}
      data-testid={testId}
      data-contentful-entry-id={contentfulEntryId}
      data-contentful-field-id={contentfulFieldId}
      {...rest}
    >
      {!hideIcon && variant !== "button" && type !== "inline" && (
        <Icon name={type} size="sm" referenceSize="link" />
      )}
      <span>{text}</span>
    </Link>
  );
}
